import { useEffect, useState } from "react";

export type Currency = "CLP" | "USD";

export type CartItem = {
  id: string;
  slug: string;
  title: string;
  price_clp: number | null;
  price_usd: number | null;
  qty: number;
};

const CART_KEY = "mc_cart_v1";
const CURR_KEY = "mc_currency_v1";
const EVT = "mc_cart_change";

function read(): CartItem[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(CART_KEY) || "[]");
  } catch {
    return [];
  }
}
function write(items: CartItem[]) {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
  window.dispatchEvent(new Event(EVT));
}

export function useCart() {
  const [items, setItems] = useState<CartItem[]>([]);
  useEffect(() => {
    setItems(read());
    const h = () => setItems(read());
    window.addEventListener(EVT, h);
    window.addEventListener("storage", h);
    return () => {
      window.removeEventListener(EVT, h);
      window.removeEventListener("storage", h);
    };
  }, []);
  return {
    items,
    count: items.reduce((s, i) => s + i.qty, 0),
    add: (it: Omit<CartItem, "qty">) => {
      const cur = read();
      const idx = cur.findIndex((x) => x.id === it.id);
      if (idx >= 0) cur[idx].qty += 1;
      else cur.push({ ...it, qty: 1 });
      write(cur);
    },
    remove: (id: string) => write(read().filter((x) => x.id !== id)),
    setQty: (id: string, qty: number) => {
      const cur = read().map((x) => (x.id === id ? { ...x, qty: Math.max(1, qty) } : x));
      write(cur);
    },
    clear: () => write([]),
  };
}

export function useCurrency() {
  const [currency, setCurrencyState] = useState<Currency>("CLP");
  useEffect(() => {
    const v = (localStorage.getItem(CURR_KEY) as Currency) || "CLP";
    setCurrencyState(v);
    const h = () => setCurrencyState((localStorage.getItem(CURR_KEY) as Currency) || "CLP");
    window.addEventListener("mc_curr_change", h);
    return () => window.removeEventListener("mc_curr_change", h);
  }, []);
  const setCurrency = (c: Currency) => {
    localStorage.setItem(CURR_KEY, c);
    window.dispatchEvent(new Event("mc_curr_change"));
  };
  return { currency, setCurrency };
}

export function formatPrice(item: { price_clp: number | null; price_usd: number | null }, currency: Currency): string {
  if (currency === "CLP") {
    return item.price_clp != null
      ? `$${item.price_clp.toLocaleString("es-CL")} CLP`
      : "Consultar precio";
  }
  return item.price_usd != null
    ? `US$${item.price_usd.toLocaleString("en-US")}`
    : "Consultar precio";
}

export function itemPrice(item: { price_clp: number | null; price_usd: number | null }, currency: Currency): number {
  return (currency === "CLP" ? item.price_clp : item.price_usd) ?? 0;
}
